import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { FormTextbox } from "../../../forms/FormTextbox";
import { IFormUUID } from "../../../models/nominals";
import { IWriteReviewTemplateField } from "../../../models/reviews";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { reviewFormTextChange } from "../reducers";
import { getWriteReviewFormState } from "../selectors";

type IProps = Immutable<{
    formUUID: IFormUUID;
    name: string;
    field: IWriteReviewTemplateField;
    isMultiReview?: boolean;
}>;

export const WriteReviewFormTextArea = (props: IProps): React.ReactNode => {
    const [isExpanded, setIsExpanded] = React.useState(false);

    const dispatch = useAppDispatch();

    const formState = useAppSelector((rootState) =>
        getWriteReviewFormState(rootState.reviews, props),
    );
    const value = formState?.valuesText[props.name] ?? "";
    const errors = formState.errors[props.name] ?? [];
    const showErrors = formState.showErrors;

    const onChange = (e: React.FormEvent<HTMLTextAreaElement>) => {
        dispatch(
            reviewFormTextChange({
                formUUID: props.formUUID,
                fieldName: props.name,
                fieldValue: e.currentTarget.value,
            }),
        );
    };

    const clickToExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const isCommentBox = props.name === "comments";

    return (
        <span>
            <FormTextbox
                name={props.name}
                id={props.name}
                required={props.field.is_required}
                label={props.field.name}
                labelPlacement={"static"}
                maxLength={props.field.max_length || undefined}
                rows={4}
                value={value}
                errors={errors}
                showErrorMessages={showErrors}
                onChange={onChange}
            />
            {isCommentBox && (
                <div className="write-review-form__comment-disclaimer">
                    {!isExpanded && props.isMultiReview ? (
                        <button
                            onClick={clickToExpand}
                            aria-expanded={isExpanded}
                        >
                            {t`+ Show tips on writing useful comments`}
                        </button>
                    ) : (
                        <>
                            <h4>
                                {t`The most useful comments contain specific information about:`}
                            </h4>
                            <ul>
                                <li>
                                    {t`How this product has changed your life`}
                                </li>
                                <li>{t`Things you love about it`}</li>
                                <li>{t`Things you don't love about it`}</li>
                            </ul>
                            {t`Please do not include: HTML, references to other retailers, pricing, personal information, or any profane, inflammatory or copyrighted comments. Also, if you were given this product as a gift or otherwise compensated in exchange for writing this review, you are REQUIRED to disclose it in your comments above.`}
                        </>
                    )}
                </div>
            )}
        </span>
    );
};
